import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import {
  CalendarDays, Package, MessageSquare, FileText, FolderOpen, Clock, User as UserIcon, BarChart3, ArrowRight,
} from 'lucide-react';

const API_URL = '';
const HIDDEN_PO_STATUSES = ['Fulfilled']; // hide completed POs from overview

function normKey(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(iso); }
}

function StatPill({ label, count, colorClass }) {
  if (!count) return null;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colorClass}`}>
      {label}: {count}
    </span>
  );
}

export default function ProjectOverviewTab({ user, onJumpTab }) {
  const [projects, setProjects] = useState([]);
  const [selectedPid, setSelectedPid] = useState('');
  const [calendar, setCalendar] = useState([]);
  const [pos, setPos] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [dprs, setDprs] = useState([]);
  const [gantt, setGantt] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`);
      setProjects(res.data || []);
      // Auto-select first project if none chosen
      if (!selectedPid && res.data?.length) setSelectedPid(res.data[0].id);
    } catch { /* ignore */ }
  }, [selectedPid]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const selectedProject = useMemo(
    () => projects.find(p => p.id === selectedPid),
    [projects, selectedPid]
  );

  // Load everything tagged to the selected project
  const loadProjectData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    const pname = selectedProject.project_name;
    const pkey = normKey(pname);
    try {
      const [calRes, poRes, rfiRes, dprRes, ganttRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendar/tasks?department=Construction`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/supply-chain/po?project_name=${encodeURIComponent(pname)}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/rfi`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/dpr`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/gantt?department=construction`).catch(() => ({ data: [] })),
      ]);

      // Calendar: by project_id OR by project_name match
      const calItems = (calRes.data || []).filter(
        t => t.project_id === selectedProject.id || normKey(t.project_name) === pkey
      );
      setCalendar(calItems);

      // POs: already filtered by project_name via query. Drop completed.
      setPos((poRes.data || []).filter(p => !HIDDEN_PO_STATUSES.includes(p.status)));

      // RFIs: filter by project (string field)
      setRfis((rfiRes.data || []).filter(r => normKey(r.project) === pkey && r.type === 'submission'));

      // DPRs: filter by project string
      setDprs((dprRes.data || []).filter(d => normKey(d.project) === pkey));

      // Gantt: any chart matching project_name
      setGantt((ganttRes.data || []).filter(g => normKey(g.project_name) === pkey));
    } finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { loadProjectData(); }, [loadProjectData]);

  // ========= Aggregates =========
  const poSummary = useMemo(() => {
    const sum = { total: pos.length, amount: 0, draft: 0, pending_approval: 0, for_payment: 0, paid_for_delivery: 0, delivered: 0 };
    pos.forEach(p => {
      sum.amount += p.amount || 0;
      if (p.status === 'Draft') sum.draft++;
      else if (p.status === 'Pending Approval') sum.pending_approval++;
      else if (p.status === 'For Payment') sum.for_payment++;
      else if (p.status === 'Paid for Delivery') sum.paid_for_delivery++;
      else if (p.status === 'Delivered') sum.delivered++;
    });
    return sum;
  }, [pos]);

  const upcomingCalendar = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    return [...calendar]
      .filter(t => t.task_date >= todayStr)
      .sort((a, b) => (a.task_date || '').localeCompare(b.task_date || ''))
      .slice(0, 6);
  }, [calendar]);

  const recentDprs = useMemo(() => {
    return [...dprs].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 3);
  }, [dprs]);

  const openRfis = useMemo(() => rfis.filter(r => r.status !== 'resolved').slice(0, 5), [rfis]);

  const activeGantt = gantt[0];

  return (
    <div className="space-y-5">
      {/* Project selector */}
      <Card className="glass-effect border-border/40" data-testid="overview-project-selector-card">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <FolderOpen className="w-5 h-5 text-primary" />
          <Label htmlFor="overview-project" className="text-sm font-semibold">Select Project:</Label>
          <select
            id="overview-project"
            data-testid="overview-project-select"
            value={selectedPid}
            onChange={e => setSelectedPid(e.target.value)}
            className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm min-w-[220px]"
          >
            <option value="">— Choose a project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.project_name}{p.project_code ? ` (${p.project_code})` : ''}</option>
            ))}
          </select>
          {selectedProject?.description && (
            <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">{selectedProject.description}</span>
          )}
        </CardContent>
      </Card>

      {!selectedProject ? (
        <Card className="glass-effect border-border/40">
          <CardContent className="py-12 text-center text-muted-foreground">
            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Select a project above to view its overview.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="overview-grid">

          {/* Schedule / Gantt summary */}
          <Card className="glass-effect border-border/40 md:col-span-2" data-testid="overview-schedule">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" /> Schedule
                </h3>
                <button className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                  onClick={() => onJumpTab?.('schedule')} data-testid="overview-jump-schedule">
                  Open <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {activeGantt ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div><div className="text-muted-foreground text-[10px] uppercase tracking-wider">Start</div><div className="font-semibold">{fmtDate(activeGantt.project_start) || '—'}</div></div>
                  <div><div className="text-muted-foreground text-[10px] uppercase tracking-wider">End</div><div className="font-semibold">{fmtDate(activeGantt.project_end) || '—'}</div></div>
                  <div><div className="text-muted-foreground text-[10px] uppercase tracking-wider">Working Days</div><div className="font-semibold">{activeGantt.project_working_days || 0}</div></div>
                  <div>
                    <div className="text-muted-foreground text-[10px] uppercase tracking-wider">Progress</div>
                    <div className="font-semibold text-primary">{activeGantt.project_progress || 0}%</div>
                    <div className="mt-1 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${activeGantt.project_progress || 0}%` }} />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No schedule yet for this project.</p>
              )}
            </CardContent>
          </Card>

          {/* Supply Chain summary */}
          <Card className="glass-effect border-border/40" data-testid="overview-supply-chain">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Package className="w-4 h-4 text-orange-500" /> Supply Chain
                </h3>
                <span className="text-[10px] text-muted-foreground">excluding Fulfilled</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-2xl font-bold text-foreground">{poSummary.total}</span>
                <span className="text-xs text-muted-foreground">open POs · ₱{poSummary.amount.toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <StatPill label="Draft" count={poSummary.draft} colorClass="bg-gray-100 text-gray-700" />
                <StatPill label="Approval" count={poSummary.pending_approval} colorClass="bg-yellow-100 text-yellow-700" />
                <StatPill label="Payment" count={poSummary.for_payment} colorClass="bg-blue-100 text-blue-700" />
                <StatPill label="Delivery" count={poSummary.paid_for_delivery} colorClass="bg-purple-100 text-purple-700" />
                <StatPill label="Delivered" count={poSummary.delivered} colorClass="bg-green-100 text-green-700" />
              </div>
              {pos.length > 0 ? (
                <div className="space-y-1">
                  {pos.slice(0, 4).map(po => (
                    <div key={po.id} className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-muted/20">
                      <span className="font-mono text-primary">{po.control_number}</span>
                      <span className="truncate flex-1 mx-2 text-muted-foreground">{po.vendor_name || '—'}</span>
                      <span className="font-semibold">₱{po.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                  {pos.length > 4 && <p className="text-[10px] text-muted-foreground text-center pt-1">+{pos.length - 4} more</p>}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No active POs.</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Calendar */}
          <Card className="glass-effect border-border/40" data-testid="overview-calendar">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-blue-500" /> Upcoming Tasks
                </h3>
                <button className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                  onClick={() => onJumpTab?.('calendar')} data-testid="overview-jump-calendar">
                  Open <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {upcomingCalendar.length > 0 ? (
                <div className="space-y-1.5">
                  {upcomingCalendar.map(t => (
                    <div key={t.id} className="text-[11px] px-2 py-1.5 rounded bg-muted/20 flex items-center gap-2">
                      <span className="font-semibold text-primary whitespace-nowrap">{fmtDate(t.task_date)}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{t.task_time || ''}</span>
                      <span className="truncate flex-1">{t.task_name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                        t.priority === 'Urgent' ? 'bg-red-100 text-red-700' :
                        t.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                        t.priority === 'Low' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'
                      }`}>{t.priority}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No upcoming tasks.</p>
              )}
            </CardContent>
          </Card>

          {/* Open RFIs */}
          <Card className="glass-effect border-border/40" data-testid="overview-rfi">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-purple-500" /> Open RFIs
                </h3>
                <button className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                  onClick={() => onJumpTab?.('rfi')} data-testid="overview-jump-rfi">
                  Open <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {openRfis.length > 0 ? (
                <div className="space-y-1.5">
                  {openRfis.map(r => (
                    <div key={r.id} className="text-[11px] px-2 py-1.5 rounded bg-muted/20">
                      <div className="font-semibold truncate">{r.title}</div>
                      <div className="text-muted-foreground flex items-center gap-2 mt-0.5">
                        <UserIcon className="w-3 h-3" />{r.submitted_by_name}
                        <Clock className="w-3 h-3 ml-1" />{fmtDate(r.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No open RFIs.</p>
              )}
            </CardContent>
          </Card>

          {/* Recent DPR */}
          <Card className="glass-effect border-border/40" data-testid="overview-dpr">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-600" /> Recent DPR
                </h3>
                <button className="text-[10px] text-primary hover:underline flex items-center gap-0.5"
                  onClick={() => onJumpTab?.('dpr')} data-testid="overview-jump-dpr">
                  Open <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {recentDprs.length > 0 ? (
                <div className="space-y-2">
                  {recentDprs.map(d => (
                    <div key={d.id} className="text-[11px] px-2 py-1.5 rounded bg-muted/20">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{fmtDate(d.created_at)}</span>
                        <span className="text-muted-foreground">by {d.submitted_by_name}</span>
                      </div>
                      <p className="text-muted-foreground mt-0.5 line-clamp-2">
                        {(d.accomplished_works || '').slice(0, 140)}{(d.accomplished_works || '').length > 140 ? '…' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No DPRs submitted yet.</p>
              )}
            </CardContent>
          </Card>

        </div>
      )}

      {loading && <p className="text-xs text-center text-muted-foreground">Loading…</p>}
    </div>
  );
}
